import React, { lazy, Suspense, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EMPIRE_ICON } from '../../theme';

// Lazy-load the heavy 3D museum bundle (pure Three.js — avoids R3F JSX-prop bug)
const MuseumLazy = lazy(() =>
  import('./MuseumScenePure')
);

/* Eager-warm the museum chunk during idle time so the very first click
 * lands instantly instead of showing a loader. Safe to call multiple
 * times — webpack returns the same cached promise. */
let _museumPreloadedPromise = null;
export const preloadMuseum = () => {
  if (!_museumPreloadedPromise) {
    _museumPreloadedPromise = import('./MuseumScenePure').catch(() => null);
  }
  return _museumPreloadedPromise;
};

const GOLD = '#D4AF37';

/**
 * DragonGateway
 * - isOpen: boolean — whether the museum is currently open
 * - onClose: () => void — called when user taps Exit
 * - originRect: DOMRect | null — bounding rect of Dragon button at click time
 *   (used to start zoom from the exact on-screen position)
 */
export default function DragonGateway({ isOpen, onClose, originRect, initialRoomId = 'hall' }) {
  const [stage, setStage] = useState('idle'); // idle → zoomIn → museum → zoomOut → idle
  const [sceneReady, setSceneReady] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStage('zoomIn');
      // After zoom completes, mount museum.  Shortened from 900→250ms
      // so the user lands in the 3D space almost instantly.
      const t = setTimeout(() => setStage('museum'), 250);
      return () => clearTimeout(t);
    } else {
      if (stage === 'museum') {
        setStage('zoomOut');
        const t = setTimeout(() => {
          setStage('idle');
          setSceneReady(false);
        }, 250);
        return () => clearTimeout(t);
      }
    }
  }, [isOpen]); // eslint-disable-line

  if (stage === 'idle') return null;

  // Compute the origin position for the zoom (where dragon was tapped)
  const origin = originRect
    ? {
        left: originRect.left + originRect.width / 2,
        top: originRect.top + originRect.height / 2,
        size: originRect.width,
      }
    : { left: window.innerWidth / 2, top: window.innerHeight / 2, size: 110 };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        pointerEvents: stage === 'museum' ? 'auto' : 'none',
        background: 'transparent',
      }}
      data-testid="dragon-gateway"
    >
      <AnimatePresence>
        {/* Black fade overlay */}
        {(stage === 'zoomIn' || stage === 'museum' || stage === 'zoomOut') && (
          <motion.div
            key="fade"
            initial={{ opacity: 0 }}
            animate={{ opacity: stage === 'zoomOut' ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: stage === 'zoomOut' ? 0.6 : 0.8, ease: 'easeInOut' }}
            style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(circle at center, #0A1635 0%, #000000 80%)',
            }}
          />
        )}

        {/* Expanding Dragon (zoom-in only) */}
        {stage === 'zoomIn' && (
          <motion.img
            key="dragon-zoom"
            src={EMPIRE_ICON.src}
            alt=""
            initial={{
              position: 'absolute',
              left: origin.left,
              top: origin.top,
              width: origin.size,
              height: origin.size,
              x: '-50%', y: '-50%',
              scale: 1,
              opacity: 1,
              borderRadius: '50%',
              boxShadow: `0 0 20px ${GOLD}cc, 0 0 60px #4a80ff88`,
              objectFit: 'cover',
            }}
            animate={{
              left: window.innerWidth / 2,
              top: window.innerHeight / 2,
              scale: 5,
              opacity: 0.3,
              boxShadow: `0 0 120px ${GOLD}, 0 0 240px #4a80ff`,
            }}
            transition={{ duration: 0.25, ease: [0.65, 0, 0.35, 1] }}
          />
        )}

        {/* 3D Museum scene */}
        {stage === 'museum' && (
          <motion.div
            key="museum"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ position: 'absolute', inset: 0 }}
          >
            <Suspense fallback={
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#04030a',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  border: `2px solid ${GOLD}`, borderTopColor: 'transparent',
                  animation: 'museumSpin 0.8s linear infinite',
                }} />
                <style>{`@keyframes museumSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>
            }>
              <MuseumLazy onExit={onClose} initialRoomId={initialRoomId} onReady={() => setSceneReady(true)} />
              {/* Exit portal — glowing Dragon Head at bottom center.
                  HIDDEN until MuseumScenePure signals its scene + HUD
                  controls (Lobby/Back button, rotate arrows) are
                  visible.  This prevents the dragon from appearing
                  on top of the "Entering the Imperial Vault" frame. */}
              {sceneReady && (
              <button
                type="button"
                onClick={() => onClose && onClose()}
                aria-label="Exit Museum"
                data-testid="museum-exit-btn"
                style={{
                  position: 'fixed',
                  left: '50%',
                  bottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 72px)',
                  transform: 'translateX(-50%)',
                  width: 72, height: 72, borderRadius: '50%',
                  border: `2px solid ${GOLD}`,
                  background: 'radial-gradient(circle at 50% 30%, #1a130a, #050308 70%)',
                  boxShadow: `0 0 18px ${GOLD}, 0 0 40px #4a80ff99, inset 0 0 10px ${GOLD}44`,
                  padding: 0, cursor: 'pointer',
                  overflow: 'hidden',
                  zIndex: 10,
                  animation: 'dragonFadeIn 0.35s ease-out both',
                }}
              >
                <img
                  src={EMPIRE_ICON.src}
                  alt=""
                  style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    objectPosition: EMPIRE_ICON.pos,
                    filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.6))',
                  }}
                />
              </button>
              )}
              <style>{`@keyframes dragonFadeIn { from { opacity: 0; transform: translateX(-50%) scale(0.6); } to { opacity: 1; transform: translateX(-50%) scale(1); } }`}</style>
            </Suspense>
          </motion.div>
        )}

        {/* Zoom-out animation (reverse of zoom-in) */}
        {stage === 'zoomOut' && (
          <motion.img
            key="dragon-zoom-out"
            src={EMPIRE_ICON.src}
            alt=""
            initial={{
              position: 'absolute',
              left: window.innerWidth / 2,
              top: window.innerHeight / 2,
              width: origin.size,
              height: origin.size,
              x: '-50%', y: '-50%',
              scale: 5,
              opacity: 0.3,
              borderRadius: '50%',
              objectFit: 'cover',
            }}
            animate={{
              left: origin.left,
              top: origin.top,
              scale: 1,
              opacity: 1,
              boxShadow: `0 0 20px ${GOLD}cc, 0 0 60px #4a80ff88`,
            }}
            transition={{ duration: 0.22, ease: [0.65, 0, 0.35, 1] }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
